import { Fragment, useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { useMediaQuery } from '../../shared/hooks/useMediaQuery';
import { useMetaTags } from '../../shared/hooks/useMetaTags';
import { getSiteUrl } from '../../shared/utils/metaTags';
import { scrollMobileWizardToStepBar } from '../apps/mynazca/mynazcaLayoutUtils';
import { brushDisplayDiameter, brushDisplayToCanvasLineWidth } from '../apps/mynazca/mynazcaBrushUtils';
import { buildMynazcaExportFitFileName, canPreviewFitOnMap } from '../apps/mynazca/mynazcaStep3';
import { loadMyNazca } from '../apps/mynazca/mynazcaLoader';
import {
  runApplyPathToFitWorker,
  runImageToPathWorker,
  type ImagePathOptions,
  type ImagePathResult,
} from '../apps/mynazca/mynazcaWorkers';
import {
  clearEditStorage,
  clearRouteStorage,
  clearSaveStorage,
  IDB_KEYS,
  idbGet,
  idbSet,
  idbDel,
  loadMapCenterFromStorage,
  loadBgCompositeColorInitial,
  loadOpenEditSectionInitial,
  loadPersistEditInitial,
  LS_KEYS,
  type BgFillColor,
  readPersistFlag,
  saveRouteOptionsToStorage,
  writePersistFlag,
} from '../apps/mynazca/persistence';
import { LoadingSpinner } from '../../shared/components/LoadingSpinner';
import {
  MyNazcaImageEditorPreview,
  type EditSection,
  type MyNazcaImageEditorPreviewProps,
} from './MyNazcaImageEditorPreview';
import { type MyNazcaImageEditorControlsProps } from './MyNazcaImageEditorControls';
import { MyNazcaImageEditorFields } from './MyNazcaImageEditorFields';
import { MyNazcaMobileStep1Editor } from './MyNazcaMobileStep1Editor';
import { MyNazcaMobileStep2Editor } from './MyNazcaMobileStep2Editor';
import { MyNazcaMobileStepLayout } from './MyNazcaMobileStepLayout';
import { mynazcaRouteCopy, mynazcaT } from '../apps/mynazca/mynazcaRouteCopy';
import { MyNazcaRouteOptionsFields } from './MyNazcaRouteOptionsFields';
import { MyNazcaMap, type MapCenter, type MapRouteLatLngs } from './MyNazcaMap';
import './MyNazcaApp.css';

type WizardStep = 1 | 2 | 3;

const BG_FILL_WHITE = '#ffffff';
const BG_FILL_BLACK = '#000000';

function fitDisplayBoxToWrap(
  wrap: HTMLElement | null,
  box: HTMLElement | null,
  pixelWidth: number,
  pixelHeight: number,
) {
  if (!wrap || !box || pixelWidth < 1 || pixelHeight < 1) return;
  const availableW = wrap.clientWidth;
  const availableH = wrap.clientHeight;
  if (availableW < 1 || availableH < 1) return;
  const scale = Math.min(availableW / pixelWidth, availableH / pixelHeight);
  const w = Math.max(1, Math.floor(pixelWidth * scale));
  const h = Math.max(1, Math.floor(pixelHeight * scale));
  const nextW = `${w}px`;
  const nextH = `${h}px`;
  if (box.style.width === nextW && box.style.height === nextH) return;
  box.style.width = nextW;
  box.style.height = nextH;
}


type TrackRemapGlobal = {
  imageFromFile: (file: File, options?: any) => Promise<any>;
  imagePreprocessGrayFromFile?: (file: File, options?: any) => Promise<any>;
  imageGrayToRgba?: (gray: any) => any;
  imageEdgePreviewFromGray?: (gray: any, options?: any) => any;
  applyPathToFIT: (imagePathResult: any, fitFileOrBuffer: File | ArrayBuffer, options?: any) => Promise<any>;
  mergeFITSensorData?: (appliedPoints: any, sensorFitFileOrBuffer: File | ArrayBuffer | Blob, options?: any) => Promise<any>;
};

declare global {
  interface Window {
    Cropper?: any;
  }
}

function getTrackRemapLib(): TrackRemapGlobal | undefined {
  return (window as unknown as { TrackRemap?: TrackRemapGlobal }).TrackRemap;
}

async function deepCopyBlob(blob: Blob): Promise<Blob> {
  return new Blob([await blob.arrayBuffer()], { type: blob.type || 'image/png' });
}

function appliedPointsToRouteLatLngs(
  points: Array<{ lat: number; lng: number }> | undefined | null,
): MapRouteLatLngs | null {
  if (!points?.length) return null;
  const line = points
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    .map((p) => [p.lat, p.lng] as [number, number]);
  return line.length >= 2 ? line : null;
}

const BG_REMOVAL_MODULE_URL = 'https://cdn.jsdelivr.net/npm/@imgly/background-removal@1.5.5/+esm';
const CROPPER_CSS_URL = 'https://unpkg.com/cropperjs@1.5.13/dist/cropper.min.css';
const CROPPER_JS_URL = 'https://unpkg.com/cropperjs@1.5.13/dist/cropper.min.js';

/** Nazca town — OSM に街・道路が見える初期中心（地上絵エリアは地図がほぼ空）。 */
const DEFAULT_MAP_CENTER: MapCenter = { lat: -14.8359, lng: -74.9389 };

const UNDO_CAP = 30;

type UndoEntry =
  | { kind: 'image'; snapshot: Blob }
  | { kind: 'tone'; brightness: number; contrast: number };

type RedoEntry =
  | { kind: 'image'; snapshot: Blob; wasAutoBgResult?: boolean }
  | { kind: 'tone'; brightness: number; contrast: number };

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

async function fileToPngBlob(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const c = document.createElement('canvas');
  c.width = bitmap.width;
  c.height = bitmap.height;
  c.getContext('2d')?.drawImage(bitmap, 0, 0);
  bitmap.close();
  return await new Promise((resolve) => c.toBlob((b) => resolve(b ?? new Blob()), 'image/png'));
}

async function applyToneToBlob(blob: Blob, brightnessPct: number, contrastPct: number): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const c = document.createElement('canvas');
  c.width = bitmap.width;
  c.height = bitmap.height;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d context is not available');
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.filter = `brightness(${brightnessPct}%) contrast(${contrastPct}%)`;
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return await new Promise((resolve) => c.toBlob((b) => resolve(b ?? new Blob()), 'image/png'));
}

async function compositeAlphaOnColor(blob: Blob, colorCss: string): Promise<Blob> {
  const fill = colorCss || '#ffffff';
  const bitmap = await createImageBitmap(blob);
  const c = document.createElement('canvas');
  c.width = bitmap.width;
  c.height = bitmap.height;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d context is not available');
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return await new Promise((resolve) => c.toBlob((b) => resolve(b ?? new Blob()), 'image/png'));
}

/** ルート生成用: 黒地に白線の画像を、線検出向けの白地に黒線へ反転する */
async function invertImageBlobForRoute(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const c = document.createElement('canvas');
  c.width = bitmap.width;
  c.height = bitmap.height;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d context is not available');
  ctx.filter = 'invert(1)';
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return await new Promise((resolve) => c.toBlob((b) => resolve(b ?? new Blob()), 'image/png'));
}

function drawImageRoutePreview(canvas: HTMLCanvasElement, path: number[][], w: number, h: number) {
  const scale = Math.min(420 / w, 420 / h, 1);
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, cw, ch);
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i < path.length; i++) {
    const x = path[i][0] * cw;
    const y = path[i][1] * ch;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
}

async function loadCropperAssets(): Promise<void> {
  if (typeof document === 'undefined') return;
  if (window.Cropper) return;

  const cssId = 'vendor-cropperjs-css';
  if (!document.getElementById(cssId)) {
    const link = document.createElement('link');
    link.id = cssId;
    link.rel = 'stylesheet';
    link.href = CROPPER_CSS_URL;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  const scriptId = 'vendor-cropperjs-js';
  const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
  if (existing) {
    if (existing.dataset.loaded === 'true') return;
    await new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Cropper.js')), { once: true });
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = CROPPER_JS_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true },
    );
    script.addEventListener('error', () => reject(new Error('Failed to load Cropper.js')), { once: true });
    document.head.appendChild(script);
  });
}

const FIT_FILE_EXTENSION = '.fit';

const WIZARD_STEPS: Array<{ step: WizardStep; labelJa: string; labelEn: string }> = [
  { step: 1, labelJa: '画像編集', labelEn: 'Image edit' },
  { step: 2, labelJa: 'ルート作成', labelEn: 'Create route' },
  { step: 3, labelJa: 'ファイル作成', labelEn: 'Create file' },
];

type MyNazcaFilePickerProps = {
  id: string;
  accept: string;
  disabled?: boolean;
  buttonLabel: string;
  fileName: string | null;
  primary?: boolean;
  requiredExtension?: string;
  onInvalidFile?: () => void;
  onFile: (file: File | null) => void;
};

function MyNazcaFilePicker({
  id,
  accept,
  disabled,
  buttonLabel,
  fileName,
  primary,
  requiredExtension,
  onInvalidFile,
  onFile,
}: MyNazcaFilePickerProps) {
  const displayLabel = fileName ?? buttonLabel;

  return (
    <div className="mynazca-app__file-picker">
      <input
        id={id}
        type="file"
        accept={accept}
        className="mynazca-app__file-input"
        disabled={disabled}
        onChange={(e) => {
          const input = e.currentTarget;
          const file = input.files?.[0] ?? null;
          if (file && requiredExtension != null) {
            const ext = requiredExtension.toLowerCase();
            if (!file.name.toLowerCase().endsWith(ext)) {
              input.value = '';
              onInvalidFile?.();
              return;
            }
          }
          onFile(file);
        }}
      />
      <label
        htmlFor={id}
        className={`mynazca-app__file-btn${primary ? ' mynazca-app__file-btn--primary' : ''}${fileName ? ' mynazca-app__file-btn--has-file' : ''}`}
        data-disabled={disabled ? '' : undefined}
        title={fileName ?? undefined}
      >
        <span className="mynazca-app__file-btn-label">{displayLabel}</span>
      </label>
    </div>
  );
}

function MyNazcaApp() {
  const lang = useLanguage();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const siteUrl = getSiteUrl();
  const appUrl = `${siteUrl}/${lang}/apps/mynazca`;

  const titleJa = 'MyNazca(α版)';
  const titleEn = 'MyNazca (alpha)';
  const title = lang === 'ja' ? titleJa : titleEn;
  const description =
    lang === 'ja'
      ? '画像から一筆書きルートを作成し、FITファイルに合成して保存できます（ブラウザ内完結）。'
      : 'Create a single-stroke route from an image and apply it to a FIT file (runs fully in your browser).';

  useMetaTags({
    title: `${title} | Version0.1`,
    description,
    ogp: {
      title: `${title} | Version0.1`,
      description,
      url: appUrl,
      type: 'website',
      siteName: 'Version0.1',
      image: '/og-image.png',
    },
    twitterCard: {
      card: 'summary' as const,
      title: `${title} | Version0.1`,
      description,
      image: '/og-image.png',
    },
    canonicalUrl: appUrl,
  });

  /* ── step state ── */
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [maxReachedStep, setMaxReachedStep] = useState<WizardStep>(1);
  const [openEditSection, setOpenEditSection] = useState<EditSection | null>(() =>
    loadPersistEditInitial() ? loadOpenEditSectionInitial() : null,
  );

  const goToStep = (step: WizardStep) => {
    setCurrentStep(step);
    setMaxReachedStep((m) => Math.max(m, step) as WizardStep);
  };

  const selectEditSection = (section: EditSection) => {
    void (async () => {
      const prev = openEditSectionRef.current ?? openEditSection;
      if (prev === 'crop' && section !== 'crop') {
        destroyCropperSafe();
      }
      if (section === 'crop') void loadCropperAssets();
      setOpenEditSection(section);
      if (section === 'background') {
        await refreshBgCanvasFromWorkingBlob();
      }
    })();
  };

  useEffect(() => {
    openEditSectionRef.current = openEditSection;
  }, [openEditSection]);

  /* ── general state ── */
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workingBlob, setWorkingBlob] = useState<Blob | null>(null);
  const [imageFileName, setImageFileName] = useState<string | null>(null);

  const [bgCompositeColor, setBgCompositeColor] = useState<BgFillColor>(() =>
    loadPersistEditInitial() ? loadBgCompositeColorInitial() : BG_FILL_WHITE,
  );
  const [brushSize, setBrushSize] = useState(22);
  const [autoBgRemoved, setAutoBgRemoved] = useState(() => {
    if (!loadPersistEditInitial()) return false;
    try {
      return localStorage.getItem(LS_KEYS.autoBgRemoved) === 'true';
    } catch {
      return false;
    }
  });
  const [toneBrightness, setToneBrightness] = useState(100);
  const [toneContrast, setToneContrast] = useState(100);

  const undoStackRef = useRef<UndoEntry[]>([]);
  const redoStackRef = useRef<RedoEntry[]>([]);
  const [historyEpoch, setHistoryEpoch] = useState(0);
  const bumpHistory = () => setHistoryEpoch((n) => n + 1);

  const toneDragStartRef = useRef<{ brightness: number; contrast: number } | null>(null);
  const toneGestureEndListenerRef = useRef<(() => void) | null>(null);
  const workingBlobRef = useRef<Blob | null>(null);
  const toneBrightnessRef = useRef(toneBrightness);
  const toneContrastRef = useRef(toneContrast);

  useEffect(() => { workingBlobRef.current = workingBlob; }, [workingBlob]);
  useEffect(() => { toneBrightnessRef.current = toneBrightness; }, [toneBrightness]);
  useEffect(() => { toneContrastRef.current = toneContrast; }, [toneContrast]);

  const [options, setOptions] = useState<ImagePathOptions>({
    routeMode: 'strokeTrace',
    maxSize: 0,
    maxPoints: 1500,
    thresholdLow: 50,
    thresholdHigh: 255,
    thinEdges: false,
    useCenterline: true,
    inkThreshold: 200,
  });

  const [imagePathResult, setImagePathResult] = useState<ImagePathResult | null>(null);
  const [fitRouteFile, setFitRouteFile] = useState<File | null>(null);
  const [fitSensorFile, setFitSensorFile] = useState<File | null>(null);
  const [exportFitBlob, setExportFitBlob] = useState<Blob | null>(null);
  const [cropperReady, setCropperReady] = useState(false);

  const [mapRouteLatLngs, setMapRouteLatLngs] = useState<MapRouteLatLngs | null>(null);

  const [persistEdit, setPersistEdit] = useState(() => loadPersistEditInitial());
  const [persistRoute, setPersistRoute] = useState(() => readPersistFlag(LS_KEYS.persistRoute, true));
  const [persistSave, setPersistSave] = useState(() => readPersistFlag(LS_KEYS.persistSave, true));

  const [fitMapCenter, setFitMapCenter] = useState<MapCenter>(() => {
    if (!readPersistFlag(LS_KEYS.persistSave, true)) return DEFAULT_MAP_CENTER;
    return loadMapCenterFromStorage() ?? DEFAULT_MAP_CENTER;
  });
  const handleFitMapCenterChange = useCallback((center: MapCenter) => {
    setFitMapCenter(center);
  }, []);

  const rejectNonFitFile = useCallback(() => {
    setError(
      lang === 'ja'
        ? '拡張子が .fit のファイルのみ選択できます。'
        : 'Only files with the .fit extension can be selected.',
    );
  }, [lang]);

  const cropperImgRef = useRef<HTMLImageElement | null>(null);
  const cropperTargetRef = useRef<HTMLImageElement | null>(null);
  const cropperInstanceRef = useRef<any>(null);
  const cropperDockRoRef = useRef<ResizeObserver | null>(null);

  const destroyCropperSafe = () => {
    const inst = cropperInstanceRef.current;
    const target = cropperTargetRef.current;
    cropperDockRoRef.current?.disconnect();
    cropperDockRoRef.current = null;
    cropperInstanceRef.current = null;
    cropperTargetRef.current = null;
    setCropperReady(false);
    if (!inst) return;
    try {
      if (target?.isConnected) inst.destroy?.();
    } catch { /* noop */ }
  };
  const bgCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const bgCanvasWrapRef = useRef<HTMLDivElement | null>(null);
  const bgCanvasDisplayBoxRef = useRef<HTMLDivElement | null>(null);
  const bgCanvasCleanupGenRef = useRef(0);
  const bgCanvasInitGenRef = useRef(0);
  const bgCanvasDirtyRef = useRef(false);
  const isLoadingImageRef = useRef(false);
  const imageSessionRef = useRef(0);
  const openEditSectionRef = useRef<EditSection | null>(null);
  const stepBarRef = useRef<HTMLElement | null>(null);
  const scrollToStepsAfterImageRef = useRef(false);
  const [brushCursorPos, setBrushCursorPos] = useState<{ x: number; y: number } | null>(null);
  const pathPreviewCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [workingPreviewUrl, setWorkingPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  /* ── persist effects ── */
  useEffect(() => {
    writePersistFlag(LS_KEYS.persistEdit, persistEdit);
  }, [persistEdit]);

  useEffect(() => {
    writePersistFlag(LS_KEYS.persistRoute, persistRoute);
  }, [persistRoute]);

  useEffect(() => {
    writePersistFlag(LS_KEYS.persistSave, persistSave);
  }, [persistSave]);

  useEffect(() => {
    if (!persistSave) return;
    try { localStorage.setItem(LS_KEYS.mapCenter, JSON.stringify(fitMapCenter)); }
    catch { /* noop */ }
  }, [fitMapCenter, persistSave]);

  useEffect(() => {
    return () => {
      destroyCropperSafe();
      const prev = previewUrlRef.current;
      previewUrlRef.current = null;
      if (prev) { try { URL.revokeObjectURL(prev); } catch { /* noop */ } }
    };
  }, []);

  const canUndo = historyEpoch >= 0 && undoStackRef.current.length > 0;
  const canRedo = historyEpoch >= 0 && redoStackRef.current.length > 0;

  useEffect(() => {
    try {
      if (persistRoute) {
        const raw = localStorage.getItem(LS_KEYS.options);
        if (raw) {
          const parsed = JSON.parse(raw) as ImagePathOptions;
          setOptions((o) => ({ ...o, ...parsed }));
        }
      }
    } catch { /* noop */ }
    const sessionAtLoad = imageSessionRef.current;
    void (async () => {
      try {
        if (!persistEdit) return;
        const blob = await idbGet<Blob>(IDB_KEYS.workingBlob);
        if (blob && sessionAtLoad === imageSessionRef.current) {
          setWorkingBlob(blob);
          workingBlobRef.current = blob;
          setImageFileName(lang === 'ja' ? '前回の作業画像' : 'Saved working image');
          void loadCropperAssets();
        }
      } catch { /* noop */ }
    })();
    void (async () => {
      try {
        if (!persistRoute) return;
        const saved = await idbGet<ImagePathResult>(IDB_KEYS.imagePathResult);
        if (saved?.path?.length) {
          setImagePathResult(saved);
        }
      } catch { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!persistRoute || imagePathResult == null) return;
    void idbSet(IDB_KEYS.imagePathResult, imagePathResult).catch(() => { /* noop */ });
  }, [persistRoute, imagePathResult]);

  useEffect(() => {
    if (currentStep !== 2 || imagePathResult == null) return;
    let cancelled = false;
    const draw = () => {
      if (cancelled || imagePathResult == null) return false;
      const canvas = pathPreviewCanvasRef.current;
      if (!canvas) return false;
      drawImageRoutePreview(canvas, imagePathResult.path, imagePathResult.width, imagePathResult.height);
      return true;
    };
    if (draw()) return;
    let tries = 0;
    const tick = () => {
      if (draw() || ++tries > 30 || cancelled) return;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => { cancelled = true; };
  }, [currentStep, imagePathResult, isMobile]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (busy != null) return;
      if (currentStep !== 1) return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const isUndo = (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z';
      const isRedo =
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z');
      if (isUndo) { e.preventDefault(); void doUndo(); }
      else if (isRedo) { e.preventDefault(); void doRedo(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, currentStep, historyEpoch, workingBlob]);

  useEffect(() => () => detachToneGestureEndListeners(), []);

  useEffect(() => {
    if (!persistRoute) return;
    saveRouteOptionsToStorage(options);
  }, [options, persistRoute]);

  useEffect(() => {
    if (!persistEdit) return;
    try { localStorage.setItem(LS_KEYS.bgCompositeColor, bgCompositeColor); }
    catch { /* noop */ }
  }, [bgCompositeColor, persistEdit]);

  useEffect(() => {
    if (!persistEdit) return;
    try { localStorage.setItem(LS_KEYS.autoBgRemoved, autoBgRemoved ? 'true' : 'false'); }
    catch { /* noop */ }
  }, [autoBgRemoved, persistEdit]);

  useEffect(() => {
    if (!persistEdit) return;
    try {
      if (openEditSection) localStorage.setItem(LS_KEYS.openEditSection, openEditSection);
      else localStorage.removeItem(LS_KEYS.openEditSection);
    } catch { /* noop */ }
  }, [openEditSection, persistEdit]);

  useEffect(() => {
    if (!persistEdit || !workingBlob) return;
    void idbSet(IDB_KEYS.workingBlob, workingBlob).catch(() => { /* noop */ });
  }, [persistEdit, workingBlob]);

  /* ── history helpers ── */
  const clearPathAndExport = () => {
    setImagePathResult(null);
    setExportFitBlob(null);
    void idbDel(IDB_KEYS.imagePathResult).catch(() => { /* noop */ });
  };

  const PRESET_DEFAULTS: Record<string, ImagePathOptions> = {
    strokeTrace: {
      routeMode: 'strokeTrace', maxSize: 0, thresholdLow: 40, thresholdHigh: 255,
      maxPoints: 1500, useCenterline: true, inkThreshold: 200, thinEdges: false,
    },
    density: {
      routeMode: 'density', maxSize: 600, thresholdLow: 80, thresholdHigh: 255,
      maxPoints: 1500, useCenterline: false, thinEdges: false,
    },
  };

  const resetEditMemory = () => {
    imageSessionRef.current += 1;
    setAutoBgRemoved(false);
    bgCanvasDirtyRef.current = false;
    bgCanvasCleanupGenRef.current += 1;
    setWorkingBlob(null);
    setImageFileName(null);
    workingBlobRef.current = null;
    undoStackRef.current = [];
    redoStackRef.current = [];
    bumpHistory();
    clearPathAndExport();
    setOpenEditSection(null);
    setToneBrightness(100);
    setToneContrast(100);
  };

  const handlePersistEditChange = async (on: boolean) => {
    if (!on) {
      const msg = lang === 'ja'
        ? '作業中の画像をこの端末から削除します。よろしいですか？'
        : 'Remove the working image from this device. Continue?';
      if (!window.confirm(msg)) return;
      await clearEditStorage();
      resetEditMemory();
    } else {
      if (workingBlob) void idbSet(IDB_KEYS.workingBlob, workingBlob);
      try {
        localStorage.setItem(LS_KEYS.bgCompositeColor, bgCompositeColor);
        localStorage.setItem(LS_KEYS.autoBgRemoved, autoBgRemoved ? 'true' : 'false');
        if (openEditSection) localStorage.setItem(LS_KEYS.openEditSection, openEditSection);
      } catch { /* noop */ }
    }
    setPersistEdit(on);
  };

  const handlePersistRouteChange = (on: boolean) => {
    if (!on) {
      const mode = options.routeMode === 'density' ? 'density' : 'strokeTrace';
      setOptions({ ...PRESET_DEFAULTS[mode] });
      clearPathAndExport();
      clearRouteStorage();
    } else {
      saveRouteOptionsToStorage(options);
      if (imagePathResult) {
        void idbSet(IDB_KEYS.imagePathResult, imagePathResult).catch(() => { /* noop */ });
      }
    }
    setPersistRoute(on);
  };

  const handlePersistSaveChange = (on: boolean) => {
    if (!on) {
      clearSaveStorage();
      setFitMapCenter(DEFAULT_MAP_CENTER);
    } else {
      try { localStorage.setItem(LS_KEYS.mapCenter, JSON.stringify(fitMapCenter)); } catch { /* noop */ }
    }
    setPersistSave(on);
  };

  type PushImageHistoryOpts = { previous?: Blob };

  const pushImageHistory = async (next: Blob, opts?: PushImageHistoryOpts) => {
    let snapPrev: Blob | null = null;
    if (opts?.previous != null) { snapPrev = await deepCopyBlob(opts.previous); }
    else {
      const prev = workingBlobRef.current ?? workingBlob;
      if (prev != null) snapPrev = await deepCopyBlob(prev);
    }
    if (snapPrev != null) {
      undoStackRef.current = [...undoStackRef.current, { kind: 'image' as const, snapshot: snapPrev }].slice(-UNDO_CAP);
    }
    redoStackRef.current = [];
    const nextIsolated = await deepCopyBlob(next);
    setWorkingBlob(nextIsolated);
    workingBlobRef.current = nextIsolated;
    clearPathAndExport();
    bumpHistory();
  };

  const commitCropToWorkingImage = async (): Promise<boolean> => {
    const inst = cropperInstanceRef.current;
    if (!inst?.getCroppedCanvas) return false;
    bgCanvasInitGenRef.current += 1;
    const canvas: HTMLCanvasElement | undefined = inst.getCroppedCanvas({
      maxWidth: 4096,
      maxHeight: 4096,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });
    if (!canvas) return false;
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to export image'))), 'image/png'),
    );
    await pushImageHistory(blob);
    destroyCropperSafe();
    return true;
  };

  const fitBgCanvasDisplayToWrap = () => {
    const canvas = bgCanvasRef.current;
    fitDisplayBoxToWrap(
      bgCanvasWrapRef.current,
      bgCanvasDisplayBoxRef.current,
      canvas?.width ?? 0,
      canvas?.height ?? 0,
    );
  };

  const detachToneGestureEndListeners = () => {
    toneGestureEndListenerRef.current?.();
    toneGestureEndListenerRef.current = null;
  };

  const commitToneDragIfChanged = () => {
    const start = toneDragStartRef.current;
    toneDragStartRef.current = null;
    if (!start) return;
    const curB = toneBrightnessRef.current;
    const curC = toneContrastRef.current;
    if (start.brightness === curB && start.contrast === curC) return;
    undoStackRef.current = [
      ...undoStackRef.current,
      { kind: 'tone' as const, brightness: start.brightness, contrast: start.contrast },
    ].slice(-UNDO_CAP);
    redoStackRef.current = [];
    bumpHistory();
  };

  const endToneGesture = () => {
    detachToneGestureEndListeners();
    commitToneDragIfChanged();
  };

  const beginToneGesture = () => {
    endToneGesture();
    toneDragStartRef.current = {
      brightness: toneBrightnessRef.current,
      contrast: toneContrastRef.current,
    };
    const onEnd = () => endToneGesture();
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    toneGestureEndListenerRef.current = () => {
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
  };

  const waitForBgCanvasMount = (): Promise<HTMLCanvasElement | null> =>
    new Promise((resolve) => {
      let tries = 0;
      const tick = () => {
        const el = bgCanvasRef.current;
        if (el) {
          resolve(el);
          return;
        }
        if (++tries > 40) {
          resolve(null);
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });

  const initBgCanvasFromBlob = async (blob: Blob) => {
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const gen = ++bgCanvasInitGenRef.current;
    const bitmap = await createImageBitmap(blob);
    if (gen !== bgCanvasInitGenRef.current) {
      bitmap.close();
      return;
    }
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    if (gen === bgCanvasInitGenRef.current) {
      fitBgCanvasDisplayToWrap();
    }
  };

  const refreshBgCanvasFromWorkingBlob = async () => {
    const blob = workingBlobRef.current;
    if (!blob) return;
    await waitForBgCanvasMount();
    if (!bgCanvasRef.current) return;
    await initBgCanvasFromBlob(blob);
  };

  const syncWorkingBlobFromBgCanvas = async (): Promise<Blob | null> => {
    const canvas = bgCanvasRef.current;
    if (!canvas) return null;
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
  };

  const applyImageHistoryState = async (blob: Blob) => {
    bgCanvasCleanupGenRef.current += 1;
    bgCanvasDirtyRef.current = false;
    const restored = await deepCopyBlob(blob);
    setWorkingBlob(restored);
    workingBlobRef.current = restored;
    clearPathAndExport();
    if (openEditSectionRef.current === 'background' && bgCanvasRef.current) {
      await initBgCanvasFromBlob(restored);
    }
  };

  const doUndo = async () => {
    const u = undoStackRef.current;
    if (u.length === 0) return;
    const top = u[u.length - 1];
    undoStackRef.current = u.slice(0, -1);
    if (top.kind === 'image') {
      const wb = workingBlobRef.current;
      if (wb != null) {
        const snap = await deepCopyBlob(wb);
        redoStackRef.current = [
          ...redoStackRef.current,
          { kind: 'image' as const, snapshot: snap, wasAutoBgResult: autoBgRemoved },
        ].slice(-UNDO_CAP);
      }
      await applyImageHistoryState(top.snapshot);
      setAutoBgRemoved(false);
    } else {
      redoStackRef.current = [
        ...redoStackRef.current,
        { kind: 'tone' as const, brightness: toneBrightnessRef.current, contrast: toneContrastRef.current },
      ].slice(-UNDO_CAP);
      setToneBrightness(top.brightness);
      setToneContrast(top.contrast);
    }
    bumpHistory();
  };

  const doRedo = async () => {
    const r = redoStackRef.current;
    if (r.length === 0) return;
    const top = r[r.length - 1];
    redoStackRef.current = r.slice(0, -1);
    if (top.kind === 'image') {
      const wb = workingBlobRef.current;
      if (wb != null) {
        const snap = await deepCopyBlob(wb);
        undoStackRef.current = [...undoStackRef.current, { kind: 'image' as const, snapshot: snap }].slice(-UNDO_CAP);
      }
      await applyImageHistoryState(top.snapshot);
      setAutoBgRemoved(!!top.wasAutoBgResult);
    } else {
      undoStackRef.current = [
        ...undoStackRef.current,
        { kind: 'tone' as const, brightness: toneBrightnessRef.current, contrast: toneContrastRef.current },
      ].slice(-UNDO_CAP);
      setToneBrightness(top.brightness);
      setToneContrast(top.contrast);
    }
    bumpHistory();
  };

  /* ── preview url ── */
  useEffect(() => {
    if (!workingBlob) {
      destroyCropperSafe();
      const prev = previewUrlRef.current;
      previewUrlRef.current = null;
      setWorkingPreviewUrl(null);
      if (prev) { try { URL.revokeObjectURL(prev); } catch { /* noop */ } }
      return;
    }

    const prev = previewUrlRef.current;
    const url = URL.createObjectURL(workingBlob);
    previewUrlRef.current = url;
    setWorkingPreviewUrl(url);
    if (prev) {
      queueMicrotask(() => {
        try { URL.revokeObjectURL(prev); } catch { /* noop */ }
      });
    }
  }, [workingBlob]);

  /* ── image import ── */
  const prepareFromImageFile = async (file: File) => {
    const session = ++imageSessionRef.current;
    isLoadingImageRef.current = true;
    bgCanvasCleanupGenRef.current += 1;
    bgCanvasDirtyRef.current = false;
    setError(null);
    setBusy(lang === 'ja' ? '初期化中…' : 'Initializing...');
    try {
      await loadMyNazca();
      if (session !== imageSessionRef.current) return;
      await loadCropperAssets();
      if (session !== imageSessionRef.current) return;
      const png = await fileToPngBlob(file);
      if (session !== imageSessionRef.current) return;
      setAutoBgRemoved(false);
      setImageFileName(file.name);
      setWorkingBlob(png);
      workingBlobRef.current = png;
      undoStackRef.current = [];
      redoStackRef.current = [];
      bumpHistory();
      clearPathAndExport();
      setToneBrightness(100);
      setToneContrast(100);
      setOpenEditSection('crop');
      if (isMobile) scrollToStepsAfterImageRef.current = true;
    } catch (e) {
      if (session === imageSessionRef.current) {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      isLoadingImageRef.current = false;
      if (session === imageSessionRef.current) setBusy(null);
    }
  };

  /* ── cropper effect ── */
  useEffect(() => {
    if (currentStep !== 1 || openEditSection !== 'crop') return;
    if (!workingPreviewUrl) return;

    let cancelled = false;
    let img: HTMLImageElement | null = null;

    const mountCropper = (target: HTMLImageElement) => {
      const activeUrl = previewUrlRef.current;
      if (cancelled || !window.Cropper || !target.isConnected || !activeUrl || target.src !== activeUrl) return;
      try {
        destroyCropperSafe();
        cropperTargetRef.current = target;
        cropperInstanceRef.current = new window.Cropper(target, {
          viewMode: 1,
          autoCropArea: 1,
          responsive: true,
          minContainerWidth: 200,
          minContainerHeight: 100,
        });
        setCropperReady(true);
        const resizeCropper = () => {
          try {
            cropperInstanceRef.current?.resize?.();
          } catch { /* noop */ }
        };
        requestAnimationFrame(() => {
          resizeCropper();
          requestAnimationFrame(() => {
            resizeCropper();
            const mobileStep = target.closest<HTMLElement>('.mynazca-app__mobile-step');
            scrollMobileWizardToStepBar(stepBarRef.current, mobileStep);
          });
        });
        const dock = target.closest('.mynazca-app__mobile-preview-dock');
        if (dock && typeof ResizeObserver !== 'undefined') {
          cropperDockRoRef.current?.disconnect();
          cropperDockRoRef.current = new ResizeObserver(() => resizeCropper());
          cropperDockRoRef.current.observe(dock);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setCropperReady(false);
      }
    };

    const waitForImg = (): Promise<HTMLImageElement | null> =>
      new Promise((resolve) => {
        let tries = 0;
        const tick = () => {
          if (cancelled) {
            resolve(null);
            return;
          }
          const el = cropperImgRef.current;
          if (el) {
            resolve(el);
            return;
          }
          if (++tries > 30) {
            resolve(null);
            return;
          }
          requestAnimationFrame(tick);
        };
        tick();
      });

    setCropperReady(false);

    void (async () => {
      try {
        await loadCropperAssets();
        if (cancelled) return;

        img = await waitForImg();
        if (!img || cancelled) return;

        const waitForImageDecoded = async (el: HTMLImageElement) => {
          if (el.complete && el.naturalWidth > 0) return;
          await new Promise<void>((resolve, reject) => {
            el.addEventListener('load', () => resolve(), { once: true });
            el.addEventListener('error', () => reject(new Error('Failed to load preview image')), { once: true });
          });
        };

        try {
          await waitForImageDecoded(img);
        } catch {
          return;
        }

        if (cancelled || img.src !== previewUrlRef.current) return;
        mountCropper(img);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setCropperReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      destroyCropperSafe();
    };
  }, [currentStep, openEditSection, workingPreviewUrl]);

  const getBrushDisplayDiameter = (_canvas: HTMLCanvasElement | null): number => brushDisplayDiameter(brushSize);

  const getBrushCanvasLineWidth = (canvas: HTMLCanvasElement): number => {
    const rect = canvas.getBoundingClientRect();
    return brushDisplayToCanvasLineWidth(
      brushSize,
      canvas.width,
      canvas.height,
      rect.width,
      rect.height,
    );
  };

  const updateBrushCursorFromEvent = useCallback((e: PointerEvent) => {
    const box = bgCanvasDisplayBoxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    setBrushCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const onBrushPointerEnter = useCallback((e: PointerEvent) => {
    updateBrushCursorFromEvent(e);
  }, [updateBrushCursorFromEvent]);

  const onBrushPointerLeave = useCallback(() => {
    setBrushCursorPos(null);
  }, []);

  const onBgCanvasPointerDown = useCallback((e: PointerEvent) => {
    updateBrushCursorFromEvent(e);
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.setPointerCapture(e.pointerId);
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * sx;
    const y = (e.clientY - rect.top) * sy;
    (canvas as HTMLCanvasElement & { __paint?: { down: boolean; x: number; y: number } }).__paint = { down: true, x, y };
  }, [updateBrushCursorFromEvent]);

  const onBgCanvasPointerMove = useCallback((e: PointerEvent) => {
    updateBrushCursorFromEvent(e);
    const canvas = bgCanvasRef.current as HTMLCanvasElement & { __paint?: { down: boolean; x: number; y: number } } | null;
    if (!canvas?.__paint?.down) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * sx;
    const y = (e.clientY - rect.top) * sy;
    const prev = canvas.__paint;
    bgCanvasDirtyRef.current = true;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.lineWidth = getBrushCanvasLineWidth(canvas);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.restore();
    canvas.__paint = { down: true, x, y };
  }, [brushSize, updateBrushCursorFromEvent]);

  const onBgCanvasPointerUp = useCallback(async (e: PointerEvent) => {
    const canvas = bgCanvasRef.current as HTMLCanvasElement & { __paint?: { down: boolean; x: number; y: number } } | null;
    if (!canvas?.__paint?.down) return;
    canvas.__paint = { down: false, x: 0, y: 0 };
    const blob = await syncWorkingBlobFromBgCanvas();
    if (blob) await pushImageHistory(blob);
    bgCanvasDirtyRef.current = false;
    try { canvas.releasePointerCapture?.(e.pointerId); }
    catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const wizardActive =
      (currentStep === 1 && workingBlob != null) || currentStep === 2 || currentStep === 3;
    if (!wizardActive) return;
    scrollToStepsAfterImageRef.current = false;
    let tries = 0;
    const alignWizard = () => {
      const bar = stepBarRef.current;
      const mobileStep = document.querySelector<HTMLElement>('.mynazca-app__mobile-step');
      if (bar) {
        scrollMobileWizardToStepBar(bar, mobileStep);
        return;
      }
      if (++tries < 40) requestAnimationFrame(alignWizard);
    };
    requestAnimationFrame(alignWizard);
  }, [isMobile, currentStep, workingBlob]);

  /* ── background canvas effects ── */
  useEffect(() => {
    if (openEditSection !== 'background') setBrushCursorPos(null);
  }, [openEditSection]);

  useEffect(() => {
    if (currentStep !== 1 || openEditSection !== 'background') return;
    void refreshBgCanvasFromWorkingBlob();
    return () => {
      bgCanvasInitGenRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, openEditSection, workingBlob]);

  useEffect(() => {
    const box = bgCanvasDisplayBoxRef.current;
    if (box) {
      box.style.width = '';
      box.style.height = '';
    }
  }, [openEditSection]);

  useEffect(() => {
    if (openEditSection !== 'background') return;
    const wrap = bgCanvasWrapRef.current;
    if (!wrap) return;
    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => fitBgCanvasDisplayToWrap());
    });
    ro.observe(wrap);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [openEditSection, workingBlob]);

  useEffect(() => {
    if (currentStep !== 1 || openEditSection !== 'background') return;
    return () => {
      if (!bgCanvasDirtyRef.current || isLoadingImageRef.current) return;
      const cleanupId = ++bgCanvasCleanupGenRef.current;
      void (async () => {
        try {
          const blob = await syncWorkingBlobFromBgCanvas();
          if (cleanupId !== bgCanvasCleanupGenRef.current) return;
          bgCanvasDirtyRef.current = false;
          if (blob) await pushImageHistory(blob);
        } catch { /* noop */ }
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, openEditSection]);

  /* ── crop actions ── */
  const applyCrop = async () => {
    if (!cropperInstanceRef.current) return;
    setError(null);
    setBusy(lang === 'ja' ? 'トリミング適用中…' : 'Applying crop...');
    try {
      const ok = await commitCropToWorkingImage();
      if (!ok) {
        throw new Error(lang === 'ja' ? 'トリミング領域を取得できませんでした' : 'Failed to get cropped area');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const rotateCropper = (deg: number) => {
    const inst = cropperInstanceRef.current;
    if (!inst?.rotate) return;
    inst.rotate(deg);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try { if (typeof inst.render === 'function') inst.render(); else inst.resize?.(); }
        catch { /* noop */ }
      });
    });
  };

  const runAutoRemoveBackground = async () => {
    const src = workingBlobRef.current ?? workingBlob;
    if (!src) return;
    const before = await deepCopyBlob(src);
    setError(null);
    setBusy(lang === 'ja' ? '背景削除中…' : 'Removing background…');
    try {
      const mod = await import(BG_REMOVAL_MODULE_URL);
      const removeBackground = (mod as any).removeBackground;
      if (typeof removeBackground !== 'function') throw new Error('removeBackground is not available');
      const b = clamp(toneBrightnessRef.current, 50, 150);
      const c = clamp(toneContrastRef.current, 50, 150);
      const toned = await applyToneToBlob(src, b, c);
      const srcForModel = await deepCopyBlob(toned);
      const outBlob: Blob = await removeBackground(srcForModel, {});
      await pushImageHistory(outBlob, { previous: before });
      bgCanvasDirtyRef.current = false;
      if (openEditSectionRef.current === 'background' && bgCanvasRef.current) {
        await initBgCanvasFromBlob(workingBlobRef.current ?? outBlob);
      }
      setAutoBgRemoved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /* ── path generation ── */
  async function buildRouteSourceFileFromBlob(blob: Blob | null): Promise<File | null> {
    if (!blob) return null;
    const b = clamp(toneBrightnessRef.current, 50, 150);
    const c = clamp(toneContrastRef.current, 50, 150);
    const toned = await applyToneToBlob(blob, b, c);
    const opaque = await compositeAlphaOnColor(toned, bgCompositeColor);
    const forWorker = bgCompositeColor === BG_FILL_BLACK
      ? await invertImageBlobForRoute(opaque)
      : opaque;
    return new File([forWorker], 'edited.png', { type: 'image/png' });
  }

  const createPathPreview = async () => {
    const blob = workingBlobRef.current;
    const routeFile = await buildRouteSourceFileFromBlob(blob);
    if (!routeFile) return;
    setError(null);
    setBusy(lang === 'ja' ? 'ルート生成中…' : 'Generating route…');
    try {
      await loadMyNazca();
      const jobKey = String(Date.now());
      const buffer = await routeFile.arrayBuffer();
      const result = await runImageToPathWorker(
        buffer,
        options,
        (p) => {
          if (typeof p === 'string') return;
          if ((p as any)?.previewPath && pathPreviewCanvasRef.current) {
            const d = p as any;
            drawImageRoutePreview(pathPreviewCanvasRef.current, d.previewPath, d.width ?? 1, d.height ?? 1);
          }
        },
        {},
        jobKey,
      );
      setImagePathResult(result);
      if (pathPreviewCanvasRef.current) {
        drawImageRoutePreview(pathPreviewCanvasRef.current, result.path, result.width, result.height);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /* ── map route preview (FIT merge for download is done on preview) ── */
  const previewRouteOnMap = async () => {
    if (!imagePathResult) {
      setError(lang === 'ja' ? '先にルートを作成してください。' : 'Create a route first.');
      return;
    }
    if (!fitRouteFile) {
      setError(lang === 'ja' ? 'ルート用のFITファイルを選択してください。' : 'Select a route FIT file.');
      return;
    }
    setError(null);
    setMapRouteLatLngs(null);
    setBusy(lang === 'ja' ? 'プレビュー中…' : 'Previewing…');
    try {
      const jobKey = String(Date.now());
      const routeArrayBuffer = await fitRouteFile.arrayBuffer();
      const sensorArrayBuffer = fitSensorFile ? await fitSensorFile.arrayBuffer() : undefined;
      const workerRes = await runApplyPathToFitWorker(
        {
          imagePathResult: { path: imagePathResult.path, width: imagePathResult.width, height: imagePathResult.height },
          routeArrayBuffer,
          sensorArrayBuffer,
          center: fitMapCenter,
        },
        {},
        jobKey,
      );
      const fitBlob = new Blob([workerRes.fitArrayBuffer], { type: 'application/octet-stream' });
      setExportFitBlob(fitBlob);
      setMapRouteLatLngs(appliedPointsToRouteLatLngs(workerRes.appliedPoints));
    } catch {
      try {
        await loadMyNazca();
        const fp = getTrackRemapLib();
        if (!fp?.applyPathToFIT) throw new Error('applyPathToFIT is not available');
        const routeResult = await fp.applyPathToFIT(
          { path: imagePathResult.path, width: imagePathResult.width, height: imagePathResult.height },
          fitRouteFile,
          { center: fitMapCenter },
        );
        setMapRouteLatLngs(appliedPointsToRouteLatLngs(routeResult.appliedPoints));
        if (fitSensorFile && typeof fp.mergeFITSensorData === 'function') {
          const merged = await fp.mergeFITSensorData(routeResult.appliedPoints, fitSensorFile, {
            routeFitForSessionLap: routeResult.fitBlob,
          });
          setExportFitBlob(merged.fitBlob ?? null);
        } else {
          setExportFitBlob(routeResult.fitBlob ?? null);
        }
      } catch (fallbackErr) {
        setError(fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr));
      }
    } finally {
      setBusy(null);
    }
  };

  const downloadFit = () => {
    if (!exportFitBlob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(exportFitBlob);
    a.download = buildMynazcaExportFitFileName(fitRouteFile?.name);
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const loadSavedOptionsForMode = (mode: 'strokeTrace' | 'density'): ImagePathOptions => {
    if (!persistRoute) return PRESET_DEFAULTS[mode];
    try {
      const key = mode === 'strokeTrace' ? LS_KEYS.optionsStrokeTrace : LS_KEYS.optionsDensity;
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw) as ImagePathOptions;
    } catch { /* noop */ }
    return PRESET_DEFAULTS[mode];
  };

  /* ── derived values ── */
  const toneFilter = `brightness(${toneBrightness}%) contrast(${toneContrast}%)`;

  const showMobileStep1Editor = isMobile && currentStep === 1 && workingBlob != null;
  const showMobileStep2Editor = isMobile && currentStep === 2;
  const showMobileStep3Editor = isMobile && currentStep === 3;
  const showMobileWizardLayout = showMobileStep1Editor || showMobileStep2Editor || showMobileStep3Editor;

  const imageEditorPreviewProps: MyNazcaImageEditorPreviewProps = {
    lang,
    workingPreviewUrl,
    openEditSection,
    bgCompositeColor,
    toneFilter,
    cropperImgRef,
    bgCanvasRef,
    bgCanvasWrapRef,
    bgCanvasDisplayBoxRef,
    brushCursorPos,
    getBrushDisplayDiameter,
    onBrushPointerEnter,
    onBrushPointerLeave,
    onBgCanvasPointerDown,
    onBgCanvasPointerMove,
    onBgCanvasPointerUp,
  };

  const imageEditorControlsProps: Omit<MyNazcaImageEditorControlsProps, 'section' | 'variant' | 'lang' | 'busy'> = {
    cropperReady,
    autoBgRemoved,
    bgCompositeColor,
    brushSize,
    toneBrightness,
    toneContrast,
    onRotateCropper: rotateCropper,
    onApplyCrop: () => void applyCrop(),
    onAutoRemoveBackground: () => void runAutoRemoveBackground(),
    onBgCompositeColorChange: setBgCompositeColor,
    onBrushSizeChange: setBrushSize,
    onToneBrightnessChange: (v) => {
      setToneBrightness(v);
      toneBrightnessRef.current = v;
    },
    onToneContrastChange: (v) => {
      setToneContrast(v);
      toneContrastRef.current = v;
    },
    onToneGestureStart: beginToneGesture,
    onToneGestureEnd: endToneGesture,
  };

  const step3Controls = (
    <div className="mynazca-app__section">
      <h3 className="mynazca-app__section-title mynazca-app__section-title--sub">
        {lang === 'ja' ? 'FITファイルを読み込む' : 'Load FIT file'}
      </h3>
      <p className="mynazca-app__muted">
        {lang === 'ja'
          ? 'ルートを合成するFITファイルを選択してください。'
          : 'Select the FIT file to merge your route into.'}
      </p>
      <p className="mynazca-app__muted">
        {lang === 'ja'
          ? '心拍数などの身体データを別に記録している場合はルート用ファイルに合成できます。'
          : 'If you recorded body data such as heart rate in a separate file, you can merge it into the route file.'}
      </p>
      <div className="mynazca-app__field">
        <span>{lang === 'ja' ? 'ルート用' : 'Route'}</span>
        <div className="mynazca-app__field-control">
          <MyNazcaFilePicker
            id={showMobileStep3Editor ? 'mynazca-route-fit-mobile' : 'mynazca-route-fit'}
            accept=".fit"
            disabled={busy != null}
            buttonLabel={lang === 'ja' ? 'ファイルを選択' : 'Choose file'}
            fileName={fitRouteFile?.name ?? null}
            primary
            requiredExtension={FIT_FILE_EXTENSION}
            onInvalidFile={rejectNonFitFile}
            onFile={(file) => {
              setFitRouteFile(file);
              if (file) setError(null);
            }}
          />
        </div>
      </div>
      <div className="mynazca-app__field">
        <span>{lang === 'ja' ? '心拍（任意）' : 'Heart rate (optional)'}</span>
        <div className="mynazca-app__field-control">
          <MyNazcaFilePicker
            id={showMobileStep3Editor ? 'mynazca-sensor-fit-mobile' : 'mynazca-sensor-fit'}
            accept=".fit"
            disabled={busy != null}
            buttonLabel={lang === 'ja' ? 'ファイルを選択' : 'Choose file'}
            fileName={fitSensorFile?.name ?? null}
            primary
            requiredExtension={FIT_FILE_EXTENSION}
            onInvalidFile={rejectNonFitFile}
            onFile={(file) => {
              setFitSensorFile(file);
              if (file) setError(null);
            }}
          />
        </div>
      </div>

      <h3 className="mynazca-app__section-title mynazca-app__section-title--sub">
        {lang === 'ja' ? '中心の選択' : 'Choose center'}
      </h3>
      <p className="mynazca-app__muted">
        {lang === 'ja'
          ? '地図上を選択してルートの中心を設定してください。'
          : 'Select a point on the map to set the route center.'}
      </p>
      <div className="mynazca-app__btnrow mynazca-app__btnrow--block">
        <button
          type="button"
          className="mynazca-app__btn-primary"
          onClick={() => void previewRouteOnMap()}
          disabled={!canPreviewFitOnMap({ busy, fitRouteFile })}
        >
          {lang === 'ja' ? 'プレビュー' : 'Preview'}
        </button>
      </div>

      <h3 className="mynazca-app__section-title mynazca-app__section-title--sub">
        {lang === 'ja' ? 'ファイルダウンロード' : 'Download file'}
      </h3>
      <p className="mynazca-app__muted">
        {lang === 'ja'
          ? '走行データを作成したルートに変換したFITファイルをダウンロードします。'
          : 'Download a FIT file with your route applied to the activity data.'}
      </p>
      <p className="mynazca-app__muted">
        {lang === 'ja'
          ? 'Garmin ConnectやStravaなどFITファイルのインポートに対応したサービスで利用してください。'
          : 'Import the file into services that support FIT uploads, such as Garmin Connect or Strava.'}
      </p>
      <div className="mynazca-app__btnrow mynazca-app__btnrow--block">
        <button type="button" className="mynazca-app__btn-primary" onClick={downloadFit} disabled={busy != null || exportFitBlob == null}>
          {lang === 'ja' ? 'ダウンロード' : 'Download'}
        </button>
      </div>
    </div>
  );

  const canGoNext =
    currentStep === 1
      ? workingBlob != null
      : currentStep === 2
        ? imagePathResult != null
        : false;

  const persistScopeEdit = lang === 'ja' ? '画像編集' : 'Image edit';
  const persistScopeRoute = lang === 'ja' ? 'ルート作成' : 'Create route';
  const persistScopeSave = lang === 'ja' ? 'ファイル作成' : 'Create file';

  /* ── JSX ── */
  return (
    <main className={`mynazca-app${showMobileWizardLayout ? ' mynazca-app--mobile-wizard' : ''}`}>
      <div className="mynazca-app__container">
        {error && (
          <div className="mynazca-app__error-banner" role="alert">
            <p className="mynazca-app__error-banner-text">{error}</p>
            <button
              type="button"
              className="mynazca-app__error-banner-close"
              onClick={() => setError(null)}
              aria-label={lang === 'ja' ? '閉じる' : 'Close'}
            >
              ×
            </button>
          </div>
        )}

        {/* header */}
        <div className="mynazca-app__header">
          <h1 className="mynazca-app__title">{title}</h1>
          <p className="mynazca-app__lead">
            {lang === 'ja'
              ? 'Garmin Watchなどのアクティビティトラッカーで記録した運動の走行ルートを仮想のルートに変換します。'
              : 'Convert activity routes recorded on trackers such as Garmin watches into virtual routes.'}
          </p>
          <p className="mynazca-app__lead">
            {lang === 'ja' ? '自分だけの地上絵を作成しましょう。' : 'Create your own ground art.'}
          </p>
          <p className="mynazca-app__lead">
            {lang === 'ja' ? (
              <>
                ※このツールはサーバーに情報を送信しません
                <br />
                ※処理はすべてブラウザ上で行われます
              </>
            ) : (
              <>
                ※This tool does not send any information to the server.
                <br />
                ※All processing runs entirely in your browser.
              </>
            )}
          </p>
          <p className="mynazca-app__help-links">
            <Link className="mynazca-app__help-link" to={`/${lang}/apps/mynazca/help/usage`}>
              {lang === 'ja' ? '使い方' : 'How to use'}
            </Link>
            <Link className="mynazca-app__help-link" to={`/${lang}/apps/mynazca/help/route-tips`}>
              {lang === 'ja' ? 'ルート作成のコツ' : 'Route tips'}
            </Link>
            <Link className="mynazca-app__help-link" to={`/${lang}/apps/mynazca/help/contribute`}>
              {lang === 'ja' ? '開発協力のお願い' : 'Contribute'}
            </Link>
          </p>
        </div>

        {/* step bar */}
        <nav
          ref={stepBarRef}
          className={`mynazca-app__step-bar${showMobileWizardLayout ? ' mynazca-app__step-bar--sticky-mobile' : ''}`}
          aria-label="Steps"
        >
          {WIZARD_STEPS.map(({ step, labelJa, labelEn }, i) => (
            <Fragment key={step}>
              {i > 0 && (
                <div
                  className={`mynazca-app__step-line ${
                    step <= maxReachedStep ? 'mynazca-app__step-line--active' : ''
                  }`}
                />
              )}
              <button
                type="button"
                className={`mynazca-app__step ${currentStep === step ? 'mynazca-app__step--current' : ''} ${
                  step < currentStep && step <= maxReachedStep ? 'mynazca-app__step--done' : ''
                }`}
                onClick={() => setCurrentStep(step)}
                disabled={step > maxReachedStep || busy != null}
                aria-current={currentStep === step ? 'step' : undefined}
              >
                <span className="mynazca-app__step-num">
                  {step < currentStep && step <= maxReachedStep ? '✓' : step}
                </span>
                <span className="mynazca-app__step-label">{lang === 'ja' ? labelJa : labelEn}</span>
              </button>
            </Fragment>
          ))}
        </nav>

        {/* main grid */}
        <div className="mynazca-app__grid-wrap">
          {busy && (
            <div className="mynazca-app__status-overlay" aria-busy="true" role="status">
              <LoadingSpinner text={busy} />
            </div>
          )}
          {showMobileStep1Editor ? (
            <MyNazcaMobileStep1Editor
              lang={lang}
              busy={busy}
              imageFileName={imageFileName}
              openEditSection={openEditSection}
              canUndo={canUndo}
              canRedo={canRedo}
              canGoNext={canGoNext}
              stepBarRef={stepBarRef}
              onSelectImage={(file) => void prepareFromImageFile(file)}
              onSelectEditSection={selectEditSection}
              onUndo={() => void doUndo()}
              onRedo={() => void doRedo()}
              onNext={() => goToStep(2)}
              previewProps={imageEditorPreviewProps}
              controlsProps={imageEditorControlsProps}
            />
          ) : showMobileStep2Editor ? (
            <MyNazcaMobileStep2Editor
              lang={lang}
              busy={busy}
              stepBarRef={stepBarRef}
              imagePathResult={imagePathResult}
              pathPreviewCanvasRef={pathPreviewCanvasRef}
              options={options}
              setOptions={setOptions}
              loadSavedOptionsForMode={loadSavedOptionsForMode}
              onCreatePathPreview={() => void createPathPreview()}
              workingBlob={workingBlob}
              canGoNext={canGoNext}
              onBack={() => setCurrentStep(1)}
              onNext={() => goToStep(3)}
            />
          ) : showMobileStep3Editor ? (
            <MyNazcaMobileStepLayout
              lang={lang}
              stepBarRef={stepBarRef}
              preview={(
                <div className="mynazca-app__section mynazca-app__section--map-wrap mynazca-app__section--mobile-dock">
                  <MyNazcaMap
                    center={fitMapCenter}
                    onCenterChange={handleFitMapCenterChange}
                    routeLatLngs={mapRouteLatLngs}
                  />
                </div>
              )}
            >
              {step3Controls}
              <div className="mynazca-app__nav mynazca-app__nav--mobile-sticky">
                <button type="button" onClick={() => setCurrentStep(2)} disabled={busy != null}>
                  {lang === 'ja' ? '← 戻る' : '← Back'}
                </button>
              </div>
            </MyNazcaMobileStepLayout>
          ) : (
          <div className="mynazca-app__grid">
          {/* ── controls panel ── */}
          <section className="mynazca-app__panel" aria-label={lang === 'ja' ? '操作' : 'Controls'}>
            {/* ── Step 1: Image & Edit ── */}
            {currentStep === 1 && (
              <div className="mynazca-app__section">
                <h2 className="mynazca-app__section-title">
                  {lang === 'ja' ? '画像を選ぶ' : 'Choose an image'}
                </h2>
                <p className="mynazca-app__muted">
                  {lang === 'ja' ? (
                    <>
                      ルートに変換したい画像を選択してください。
                      <br />
                      写真の場合は
                      <a href="https://gemini.google.com" target="_blank" rel="noopener noreferrer">Gemini</a>
                      などで「白地に黒の一筆書き」に変換しておくのがおすすめです。
                    </>
                  ) : (
                    <>
                      Select an image to convert into a route.
                      <br />
                      For photos, we recommend converting to black line art on a white background beforehand using tools like{' '}
                      <a href="https://gemini.google.com" target="_blank" rel="noopener noreferrer">Gemini</a>.
                    </>
                  )}
                </p>
                <MyNazcaFilePicker
                  id="mynazca-image"
                  accept="image/*"
                  disabled={busy != null}
                  buttonLabel={lang === 'ja' ? 'ファイルを選択' : 'Choose file'}
                  fileName={imageFileName}
                  primary
                  onFile={(f) => {
                    if (f) void prepareFromImageFile(f);
                  }}
                />

                {workingBlob != null && !isMobile && (
                  <>
                    <h2 className="mynazca-app__section-title mynazca-app__section-title--sub">
                      {lang === 'ja' ? '画像を編集する' : 'Edit image'}
                    </h2>
                    <p className="mynazca-app__muted">
                      {lang === 'ja'
                        ? '不要な部分を削除し、できるだけ対象だけが写っている状態にしてください。'
                        : 'Remove unnecessary parts so only the subject remains visible as much as possible.'}
                    </p>

                    <MyNazcaImageEditorFields
                      lang={lang}
                      busy={busy}
                      activeSection={openEditSection ?? 'crop'}
                      canUndo={canUndo}
                      canRedo={canRedo}
                      onSelectSection={selectEditSection}
                      onUndo={() => void doUndo()}
                      onRedo={() => void doRedo()}
                      controlsProps={imageEditorControlsProps}
                    />
                                    </>
                )}
              </div>
            )}

            {/* ── Step 2: Route ── */}
            {currentStep === 2 && (
              <div className="mynazca-app__section mynazca-app__section--route-create">
                <h2 className="mynazca-app__section-title">
                  {mynazcaT(mynazcaRouteCopy.sectionTitle, lang)}
                </h2>
                <p className="mynazca-app__muted">
                  {mynazcaT(mynazcaRouteCopy.sectionHint, lang)}
                </p>

                <MyNazcaRouteOptionsFields
                  lang={lang}
                  busy={busy}
                  workingBlob={workingBlob}
                  options={options}
                  setOptions={setOptions}
                  loadSavedOptionsForMode={loadSavedOptionsForMode}
                  onCreatePathPreview={() => void createPathPreview()}
                />
              </div>
            )}

            {/* ── Step 3: Export ── */}
            {currentStep === 3 && step3Controls}

                        {/* ── nav buttons ── */}
            {!showMobileWizardLayout && ((currentStep === 1 && workingBlob != null) || currentStep > 1) && (
              <div
                className={`mynazca-app__nav${currentStep === 1 ? ' mynazca-app__nav--end' : ''}`}
              >
                {currentStep > 1 && (
                  <button type="button" onClick={() => setCurrentStep((s) => (s - 1) as WizardStep)} disabled={busy != null}>
                    {lang === 'ja' ? '← 戻る' : '← Back'}
                  </button>
                )}
                {currentStep < 3 && (
                  <button type="button" className="mynazca-app__btn-primary" onClick={() => goToStep((currentStep + 1) as WizardStep)} disabled={!canGoNext || busy != null}>
                    {lang === 'ja' ? '次へ →' : 'Next →'}
                  </button>
                )}
              </div>
            )}
          </section>

          <section
            className="mynazca-app__panel mynazca-app__panel--preview"
            aria-label={lang === 'ja' ? 'プレビュー' : 'Preview'}
          >
            {currentStep === 2 ? (
              <div className="mynazca-app__section mynazca-app__section--path-preview">
                <h2 className="mynazca-app__section-title">
                  {mynazcaT(mynazcaRouteCopy.previewPanelTitle, lang)}
                </h2>
                {imagePathResult == null && (
                  <p className="mynazca-app__muted">
                    {mynazcaT(mynazcaRouteCopy.previewEmpty, lang)}
                  </p>
                )}
                <canvas ref={pathPreviewCanvasRef} className="mynazca-app__path-canvas" />
              </div>
            ) : currentStep === 3 ? (
              <div className="mynazca-app__section mynazca-app__section--map-wrap">
                <h2 className="mynazca-app__section-title">{lang === 'ja' ? '地図（ルート中心）' : 'Map (route center)'}</h2>
                <MyNazcaMap
                  center={fitMapCenter}
                  onCenterChange={handleFitMapCenterChange}
                  routeLatLngs={mapRouteLatLngs}
                />
              </div>
            ) : (
              <MyNazcaImageEditorPreview {...imageEditorPreviewProps} />
            )}
          </section>
          </div>
          )}
        </div>

        <footer
          className="mynazca-app__persist-footer"
          aria-label={lang === 'ja' ? '編集内容の保存' : 'Save edits'}
        >
          <h2 className="mynazca-app__persist-footer-title">
            {lang === 'ja' ? '編集内容の保存' : 'Save edits'}
          </h2>
          <div className="mynazca-app__persist-list">
            <label className="mynazca-app__persist-row">
              <input
                type="checkbox"
                checked={persistEdit}
                onChange={(e) => void handlePersistEditChange(e.target.checked)}
                disabled={busy != null}
              />
              <span>{persistScopeEdit}</span>
            </label>
            <label className="mynazca-app__persist-row">
              <input
                type="checkbox"
                checked={persistRoute}
                onChange={(e) => handlePersistRouteChange(e.target.checked)}
                disabled={busy != null}
              />
              <span>{persistScopeRoute}</span>
            </label>
            <label className="mynazca-app__persist-row">
              <input
                type="checkbox"
                checked={persistSave}
                onChange={(e) => handlePersistSaveChange(e.target.checked)}
                disabled={busy != null}
              />
              <span>{persistScopeSave}</span>
            </label>
          </div>
        </footer>

      </div>
    </main>
  );
}

export default MyNazcaApp;
